import { Button, Card, CardContent, Grid, MenuItem, Select, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { FaEdit } from 'react-icons/fa';
import MultiSelect from 'react-select';
import InputMask from 'react-input-mask';
import Notification from '../../components/Notification/Notification';
import SelectDate from '../../components/SelectDate';
import api from '../../services/api';

const styleMultiSelect = {
    control: (base) => ({
        ...base,
        position: 'relative',
        minHeight: 56,
        width: '100%',
        marginTop: '0.5rem',
        minWidth: 35
    })
};

const classes = {
    root: {
        flexGrow: 1,
        margin: 20,
        marginTop: 30,
        boxShadow: 'none',
        backgroundColor: '#F5F5F5',
    },
    textTitle: {
        margin: 8,
        color: '#103168',
        fontWeight: 600,
        marginBottom: 30,
    },
    message: {
        padding: 10,
        backgroundColor: '#FFFBE8',
        color: 'rgba(0,0,0,0.8)',
        borderRadius: 7,
        width: '100%'
    },
    subTitle: {
        color: '#103168',
        fontWeight: 600,
        marginTop: 20,
        marginBottom: 5,
    },
    buttonConfirm: {
        backgroundColor: '#1551B3',
        color: '#fff',
        borderRadius: 20,
        height: '45px',
        textTransform: 'capitalize',
        fontWeight: 'bold',
        fontSize: 15,
        marginTop: '17px',
        minWidth: '220px'
    },
    buttonAdd: {
        color: '#1551B3',
        borderColor: '#1551B3',
        borderRadius: 20,
        textTransform: 'capitalize',
        fontWeight: 'bold',
    },
    labelInput: {
        color: '#333333',
        fontWeight: 'bold',
        paddingLeft: 2,
        fontSize: 14,
        marginBottom: '0.6rem'
    },
    inputText: {
        backgroundColor: 'white'
    },
    sectionBox: {
        border: '1px solid #d5d5d5',
        borderRadius: 8,
        padding: 16,
        marginTop: 8,
    }
}

const priorities = [
    { value: 'NORMAL', label: 'Normal' },
    { value: 'URGENT', label: 'Urgente' },
    { value: 'EMERGENCY', label: 'Emergencial' },
];

const statuses = [
    { value: 'OPEN', label: 'Aberta' },
    { value: 'IN_PROGRESS', label: 'Em Andamento' },
    { value: 'COMPLETED', label: 'Concluída' },
];

const zones = [
    { value: 'URBAN', label: 'Urbana' },
    { value: 'RURAL', label: 'Rural' },
];

const extensionUnits = [
    { value: 'M', label: 'm' },
    { value: 'KM', label: 'km' },
];

const servicesOptions = [
    { value: 'POTHOLE_REPAIR', label: 'Tapa-buracos' },
    { value: 'URBAN_PAVING', label: 'Asfaltamento de vias urbanas' },
    { value: 'RURAL_PAVING', label: 'Asfaltamento de vias rurais' },
    { value: 'RURAL_ROAD_MAINTENANCE', label: 'Manutenção de estradas rurais não pavimentadas' },
    { value: 'URBAN_ELECTRICAL_MAINTENANCE', label: 'Manutenção de rede elétrica urbana' },
    { value: 'RURAL_ELECTRICAL_MAINTENANCE', label: 'Manutenção de rede elétrica rural' },
    { value: 'STREET_CLEANING', label: 'Limpeza de vias públicas' },
    { value: 'PUBLIC_SPACE_CLEANING', label: 'Limpeza de espaços públicos' },
    { value: 'STORMWATER_DRAINAGE', label: 'Drenagem de águas pluviais' },
    { value: 'CURB_INSTALLATION', label: 'Execução de meios-fio' },
    { value: 'STORM_DRAIN_CLEANING', label: 'Limpeza de boca de lobo' },
    { value: 'STORM_DRAIN_CONSTRUCTION', label: 'Construção de boca de lobo' },
    { value: 'PUBLIC_SPACE_MAINTENANCE', label: 'Manutenção de espaços públicos' },
    { value: 'OTHER', label: 'Outros serviços correlatos' },
];

const emptyAsset = { description: '', plateOrPrefix: '', operatorName: '', estimatedUsage: '', actualUsage: '' };

export default function ServiceOrderRegister({ setRefresh, serviceOrder, onSaved }) {
    /* Quando recebe uma ocorrência, o formulário passa a funcionar em modo de edição */
    const isEdit = Boolean(serviceOrder);

    const initialDate = (value) => (value ? new Date(value) : null);
    const initialText = (value) => (value === undefined || value === null ? '' : String(value));

    const [orderNumber, setOrderNumber] = useState(initialText(serviceOrder?.orderNumber));
    const [issuedAt, setIssuedAt] = useState(initialDate(serviceOrder?.issuedAt));
    const [executionDeadlineDays, setExecutionDeadlineDays] = useState(initialText(serviceOrder?.executionDeadlineDays));
    const [maxCompletionDate, setMaxCompletionDate] = useState(initialDate(serviceOrder?.maxCompletionDate));
    const [priority, setPriority] = useState(serviceOrder?.priority ?? 'NORMAL');
    const [status, setStatus] = useState(serviceOrder?.status ?? 'OPEN');
    const [requesterOrigin, setRequesterOrigin] = useState(initialText(serviceOrder?.requesterOrigin));

    const [street, setStreet] = useState(initialText(serviceOrder?.location?.street));
    const [zone, setZone] = useState(serviceOrder?.location?.zone ?? 'URBAN');
    const [neighborhood, setNeighborhood] = useState(initialText(serviceOrder?.location?.neighborhood));
    const [extension, setExtension] = useState(initialText(serviceOrder?.location?.extension));
    const [extensionUnit, setExtensionUnit] = useState(serviceOrder?.location?.extensionUnit ?? 'M');
    const [referencePoint, setReferencePoint] = useState(initialText(serviceOrder?.location?.referencePoint));
    const [latitude, setLatitude] = useState(initialText(serviceOrder?.location?.latitude));
    const [longitude, setLongitude] = useState(initialText(serviceOrder?.location?.longitude));

    const [services, setServices] = useState(
        (serviceOrder?.services ?? []).map(value => (
            servicesOptions.find(option => option.value === value) ?? { value, label: value }
        ))
    );
    const [serviceDescription, setServiceDescription] = useState(initialText(serviceOrder?.serviceDescription));

    const [engineerName, setEngineerName] = useState(initialText(serviceOrder?.engineer?.name));
    const [engineerCrea, setEngineerCrea] = useState(initialText(serviceOrder?.engineer?.crea));
    const [engineerRegistration, setEngineerRegistration] = useState(initialText(serviceOrder?.engineer?.registration));

    const [supervisorName, setSupervisorName] = useState(initialText(serviceOrder?.supervisor?.name));
    const [supervisorRegistration, setSupervisorRegistration] = useState(initialText(serviceOrder?.supervisor?.registration));
    const [supervisorPhone, setSupervisorPhone] = useState(initialText(serviceOrder?.supervisor?.phone));

    /* Catálogo de materiais cadastrados (coleção separada, gerida pelo admin) */
    const [materialsCatalog, setMaterialsCatalog] = useState([]);

    /* Materiais selecionados na ocorrência, com as quantidades por item */
    const [materials, setMaterials] = useState(
        (serviceOrder?.materials ?? []).map(material => ({
            materialId: initialText(material.materialId),
            name: initialText(material.name),
            unit: initialText(material.unit),
            plannedQuantity: initialText(material.plannedQuantity),
            usedQuantity: initialText(material.usedQuantity),
        }))
    );

    useEffect(() => {
        api.get('materials')
            .then(response => setMaterialsCatalog(response.data ?? []))
            .catch(() => setMaterialsCatalog([]));
    }, []);

    const materialOptions = materialsCatalog.map(material => ({
        value: material.id,
        label: `${material.name}${material.unit ? ` (${material.unit})` : ''}`,
        name: material.name,
        unit: material.unit,
    }));

    /* Mantém no MultiSelect os materiais já selecionados */
    const selectedMaterialOptions = materials.map(material => (
        materialOptions.find(option => option.value === material.materialId)
        ?? { value: material.materialId, label: material.name, name: material.name, unit: material.unit }
    ));

    /* Ao (des)selecionar, preserva as quantidades já digitadas dos itens mantidos */
    const handleSelectMaterials = (selected) => {
        setMaterials((selected ?? []).map(option => {
            const existing = materials.find(material => material.materialId === option.value);
            return existing ?? {
                materialId: option.value,
                name: option.name,
                unit: option.unit ?? '',
                plannedQuantity: '',
                usedQuantity: '',
            };
        }));
    }
    const [allocatedAssets, setAllocatedAssets] = useState(
        serviceOrder?.allocatedAssets?.length
            ? serviceOrder.allocatedAssets.map(asset => ({
                description: initialText(asset.description),
                plateOrPrefix: initialText(asset.plateOrPrefix),
                operatorName: initialText(asset.operatorName),
                estimatedUsage: initialText(asset.estimatedUsage),
                actualUsage: initialText(asset.actualUsage),
            }))
            : [{ ...emptyAsset }]
    );

    const [startedAt, setStartedAt] = useState(initialDate(serviceOrder?.execution?.startedAt));
    const [completedAt, setCompletedAt] = useState(initialDate(serviceOrder?.execution?.completedAt));
    const [technicalNotes, setTechnicalNotes] = useState(initialText(serviceOrder?.execution?.technicalNotes));

    const [notify, setNotify] = useState({ isOpen: false, message: '', type: '', title: '' });

    const handleChangeMaterial = (index, field, value) => {
        setMaterials(materials.map((material, i) => (
            i === index ? { ...material, [field]: value } : material
        )));
    }

    const handleChangeAsset = (index, field, value) => {
        setAllocatedAssets(allocatedAssets.map((asset, i) => (
            i === index ? { ...asset, [field]: value } : asset
        )));
    }

    /* Envia apenas os campos preenchidos, evitando linhas em branco na coleção */
    const isFilled = (row) => Object.values(row).some(value => value !== '');

    const toNumber = (value) => (value === '' ? undefined : Number(value));
    const toDate = (value) => (value ? new Date(value) : undefined);

    /* Tenta obter a localização do navegador; resolve null se indisponível/negada */
    const getBrowserCoords = () => new Promise((resolve) => {
        if (!navigator.geolocation) {
            resolve(null);
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => resolve({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
            }),
            () => resolve(null),
            { enableHighAccuracy: true, timeout: 10000 }
        );
    });

    const handleSubmit = async (e) => {
        e.preventDefault();

        /* Todos os campos do formulário são obrigatórios.
           Status e Situação Final são controlados pelo administrador,
           por isso ficam de fora quando o próprio criador edita a ocorrência. */
        const requiredFields = [
            [orderNumber, 'Nº da Ordem de Serviço'],
            [issuedAt, 'Data de Emissão'],
            [executionDeadlineDays, 'Prazo de Execução (dias)'],
            [maxCompletionDate, 'Data Máxima de Conclusão'],
            [priority, 'Prioridade'],
            ...(isEdit ? [] : [
                [status, 'Status'],
            ]),
            [requesterOrigin, 'Solicitante / Origem'],
            [street, 'Logradouro / Via Pública'],
            [zone, 'Zona'],
            [neighborhood, 'Bairro / Localidade / Vila'],
            [extension, 'Trecho / Extensão'],
            [extensionUnit, 'Unidade'],
            [services.length > 0 ? services : '', 'Serviços a serem executados'],
            [serviceDescription, 'Descrição Detalhada do Trabalho'],
            [engineerName, 'Engenheiro / Fiscal Responsável'],
            [engineerCrea, 'CREA'],
            [engineerRegistration, 'Matrícula do Engenheiro'],
            [supervisorName, 'Encarregado / Líder de Equipe'],
            [supervisorRegistration, 'Matrícula do Encarregado'],
            [supervisorPhone, 'Tel. de Contato'],
            [startedAt, 'Data Início Efetivo'],
            [completedAt, 'Data Conclusão Efetiva'],
            [technicalNotes, 'Observações / Justificativas Técnicas'],
        ];

        const missing = requiredFields.find(([value]) => (
            value === '' || value === null || value === undefined
        ));

        if (missing) {
            setNotify({
                isOpen: true,
                message: `Preencha o campo obrigatório: ${missing[1]}.`,
                type: 'error',
                title: 'Campos obrigatórios'
            });
            return;
        }

        /* Latitude/Longitude são facultativas: se não preenchidas, tenta usar
           a localização coletada do navegador do usuário */
        let finalLatitude = toNumber(latitude);
        let finalLongitude = toNumber(longitude);

        if (finalLatitude === undefined || finalLongitude === undefined) {
            const coords = await getBrowserCoords();
            if (coords) {
                if (finalLatitude === undefined) finalLatitude = coords.latitude;
                if (finalLongitude === undefined) finalLongitude = coords.longitude;
            }
        }

        const data = {
            orderNumber,
            issuedAt: toDate(issuedAt),
            executionDeadlineDays: toNumber(executionDeadlineDays),
            maxCompletionDate: toDate(maxCompletionDate),
            priority,
            /* Status só é definido/alterado pelo administrador */
            ...(isEdit ? {} : { status }),
            requesterOrigin,
            location: {
                street,
                zone,
                neighborhood,
                extension: toNumber(extension),
                extensionUnit,
                referencePoint,
                latitude: finalLatitude,
                longitude: finalLongitude,
            },
            services: services.map(service => service.value),
            serviceDescription,
            engineer: {
                name: engineerName,
                crea: engineerCrea,
                registration: engineerRegistration,
            },
            supervisor: {
                name: supervisorName,
                registration: supervisorRegistration,
                phone: supervisorPhone,
            },
            materials: materials.filter(isFilled).map(material => ({
                ...material,
                plannedQuantity: toNumber(material.plannedQuantity),
                usedQuantity: toNumber(material.usedQuantity),
            })),
            allocatedAssets: allocatedAssets.filter(isFilled).map(asset => ({
                ...asset,
                estimatedUsage: toNumber(asset.estimatedUsage),
                actualUsage: toNumber(asset.actualUsage),
            })),
            execution: {
                startedAt: toDate(startedAt),
                completedAt: toDate(completedAt),
                /* Situação Final é controlada exclusivamente pelo administrador */
                technicalNotes,
            },
        };

        const request = isEdit
            ? api.patch(`/service-orders/${serviceOrder.id}`, data)
            : api.post('/service-orders', data);

        request
            .then((response) => {
                setNotify({
                    isOpen: true,
                    message: isEdit
                        ? 'Ocorrência interna atualizada com sucesso.'
                        : 'Ocorrência interna cadastrada com sucesso.',
                    type: 'success',
                    title: isEdit ? 'Atualizado' : 'Cadastrado'
                });

                if (isEdit) {
                    if (onSaved) onSaved(data);
                } else {
                    clearFields();
                    if (setRefresh) setRefresh(true);
                }
            })
            .catch((err) => {
                setNotify({
                    isOpen: true,
                    message: isEdit
                        ? 'O sistema apresentou um erro na atualização'
                        : 'O sistema apresentou um erro no cadastro',
                    type: 'error',
                    title: 'Erro'
                });
            })
    }

    const clearFields = () => {
        setOrderNumber('');
        setIssuedAt(null);
        setExecutionDeadlineDays('');
        setMaxCompletionDate(null);
        setPriority('NORMAL');
        setStatus('OPEN');
        setRequesterOrigin('');
        setStreet('');
        setZone('URBAN');
        setNeighborhood('');
        setExtension('');
        setExtensionUnit('M');
        setReferencePoint('');
        setLatitude('');
        setLongitude('');
        setServices([]);
        setServiceDescription('');
        setEngineerName('');
        setEngineerCrea('');
        setEngineerRegistration('');
        setSupervisorName('');
        setSupervisorRegistration('');
        setSupervisorPhone('');
        setMaterials([]);
        setAllocatedAssets([{ ...emptyAsset }]);
        setStartedAt(null);
        setCompletedAt(null);
        setTechnicalNotes('');
    }

    return (
        <div>
            <form onSubmit={handleSubmit}>
                <Card style={classes.root}>
                    <CardContent id='form-inputs'>

                        <Typography variant="h5" align="center" style={classes.textTitle}>
                            {isEdit ? 'Editar Ocorrência Interna' : 'Cadastro de Ocorrência Interna'}
                        </Typography>

                        <div style={classes.message}>
                            {isEdit
                                ? 'Atualize abaixo as informações da sua ocorrência interna. O status e a situação final são definidos pela administração.'
                                : 'Preencha o formulário abaixo para cadastrar uma nova ocorrência interna.'}
                        </div>

                        {/* 1. Identificação */}
                        <Typography style={classes.subTitle}>
                            Identificação
                        </Typography>

                        <div style={classes.sectionBox}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={4}>
                                <Typography style={classes.labelInput}>
                                    Nº da Ordem de Serviço: *
                                </Typography>

                                <TextField
                                    style={classes.inputText}
                                    fullWidth
                                    required
                                    value={orderNumber}
                                    onChange={e => setOrderNumber(e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <SelectDate
                                    title="Data de Emissão: *"
                                    date={issuedAt}
                                    setDate={setIssuedAt}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Typography style={classes.labelInput}>
                                    Prazo de Execução (dias): *
                                </Typography>

                                <TextField
                                    style={classes.inputText}
                                    fullWidth
                                    type='number'
                                    required
                                    value={executionDeadlineDays}
                                    onChange={e => setExecutionDeadlineDays(e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <SelectDate
                                    title="Data Máxima de Conclusão: *"
                                    date={maxCompletionDate}
                                    setDate={setMaxCompletionDate}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Typography style={classes.labelInput}>
                                    Prioridade: *
                                </Typography>

                                <Select
                                    style={classes.inputText}
                                    fullWidth
                                    value={priority}
                                    onChange={e => setPriority(e.target.value)}
                                >
                                    {priorities.map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Grid>

                            {!isEdit && (
                                <Grid item xs={12} md={4}>
                                    <Typography style={classes.labelInput}>
                                        Status: *
                                    </Typography>

                                    <Select
                                        style={classes.inputText}
                                        fullWidth
                                        value={status}
                                        onChange={e => setStatus(e.target.value)}
                                    >
                                        {statuses.map(option => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Grid>
                            )}

                            <Grid item xs={12} md={12}>
                                <Typography style={classes.labelInput}>
                                    Solicitante / Origem: *
                                </Typography>

                                <TextField
                                    style={classes.inputText}
                                    fullWidth
                                    required
                                    value={requesterOrigin}
                                    onChange={e => setRequesterOrigin(e.target.value)}
                                />
                            </Grid>
                        </Grid>

                        </div>

                        {/* 2. Localização */}
                        <Typography style={classes.subTitle}>
                            Localização
                        </Typography>

                        <div style={classes.sectionBox}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={6}>
                                <Typography style={classes.labelInput}>
                                    Logradouro / Via Pública: *
                                </Typography>

                                <TextField
                                    style={classes.inputText}
                                    fullWidth
                                    required
                                    value={street}
                                    onChange={e => setStreet(e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <Typography style={classes.labelInput}>
                                    Zona: *
                                </Typography>

                                <Select
                                    style={classes.inputText}
                                    fullWidth
                                    value={zone}
                                    onChange={e => setZone(e.target.value)}
                                >
                                    {zones.map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <Typography style={classes.labelInput}>
                                    Bairro / Localidade / Vila: *
                                </Typography>

                                <TextField
                                    style={classes.inputText}
                                    fullWidth
                                    required
                                    value={neighborhood}
                                    onChange={e => setNeighborhood(e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <Typography style={classes.labelInput}>
                                    Trecho / Extensão: *
                                </Typography>

                                <TextField
                                    style={classes.inputText}
                                    fullWidth
                                    type='number'
                                    required
                                    value={extension}
                                    onChange={e => setExtension(e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <Typography style={classes.labelInput}>
                                    Unidade: *
                                </Typography>

                                <Select
                                    style={classes.inputText}
                                    fullWidth
                                    value={extensionUnit}
                                    onChange={e => setExtensionUnit(e.target.value)}
                                >
                                    {extensionUnits.map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Typography style={classes.labelInput}>
                                    Ponto de Referência / GPS:
                                </Typography>

                                <TextField
                                    style={classes.inputText}
                                    fullWidth
                                    value={referencePoint}
                                    onChange={e => setReferencePoint(e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Typography style={classes.labelInput}>
                                    Latitude:
                                </Typography>

                                <TextField
                                    style={classes.inputText}
                                    fullWidth
                                    type='number'
                                    value={latitude}
                                    onChange={e => setLatitude(e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Typography style={classes.labelInput}>
                                    Longitude:
                                </Typography>

                                <TextField
                                    style={classes.inputText}
                                    fullWidth
                                    type='number'
                                    value={longitude}
                                    onChange={e => setLongitude(e.target.value)}
                                />
                            </Grid>
                        </Grid>

                        </div>

                        {/* 3. Especificação dos serviços */}
                        <Typography style={classes.subTitle}>
                            Especificação dos Serviços a Serem Executados
                        </Typography>

                        <div style={classes.sectionBox}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={12}>
                                <Typography style={classes.labelInput}>
                                    Selecione um ou mais serviços a serem executados: *
                                </Typography>

                                <MultiSelect isMulti
                                    options={servicesOptions}
                                    styles={styleMultiSelect}
                                    value={services}
                                    onChange={e => setServices(e)}
                                    placeholder=""
                                />
                            </Grid>

                            <Grid item xs={12} md={12}>
                                <Typography style={classes.labelInput}>
                                    Descrição Detalhada do Trabalho / Metragem / Especificações Complementares: *
                                </Typography>

                                <TextField
                                    style={classes.inputText}
                                    fullWidth
                                    multiline
                                    rows={4}
                                    required
                                    value={serviceDescription}
                                    onChange={e => setServiceDescription(e.target.value)}
                                />
                            </Grid>
                        </Grid>

                        </div>

                        {/* 4. Responsáveis */}
                        <Typography style={classes.subTitle}>
                            Responsáveis
                        </Typography>

                        <div style={classes.sectionBox}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={4}>
                                <Typography style={classes.labelInput}>
                                    Engenheiro / Fiscal Responsável: *
                                </Typography>

                                <TextField
                                    style={classes.inputText}
                                    fullWidth
                                    required
                                    value={engineerName}
                                    onChange={e => setEngineerName(e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Typography style={classes.labelInput}>
                                    CREA: *
                                </Typography>

                                <TextField
                                    style={classes.inputText}
                                    fullWidth
                                    required
                                    value={engineerCrea}
                                    onChange={e => setEngineerCrea(e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Typography style={classes.labelInput}>
                                    Matrícula do Engenheiro: *
                                </Typography>

                                <TextField
                                    style={classes.inputText}
                                    fullWidth
                                    required
                                    value={engineerRegistration}
                                    onChange={e => setEngineerRegistration(e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Typography style={classes.labelInput}>
                                    Encarregado / Líder de Equipe: *
                                </Typography>

                                <TextField
                                    style={classes.inputText}
                                    fullWidth
                                    required
                                    value={supervisorName}
                                    onChange={e => setSupervisorName(e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Typography style={classes.labelInput}>
                                    Matrícula do Encarregado: *
                                </Typography>

                                <TextField
                                    style={classes.inputText}
                                    fullWidth
                                    required
                                    value={supervisorRegistration}
                                    onChange={e => setSupervisorRegistration(e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Typography style={classes.labelInput}>
                                    Tel. de Contato: *
                                </Typography>

                                <InputMask
                                    mask="(99) 9 9999-9999"
                                    maskChar=" "
                                    type="tel"
                                    value={supervisorPhone}
                                    onChange={e => setSupervisorPhone(e.target.value)}
                                >
                                    {() => (
                                        <TextField
                                            style={classes.inputText}
                                            fullWidth
                                            required
                                        />
                                    )}
                                </InputMask>
                            </Grid>
                        </Grid>

                        </div>

                        {/* 5. Materiais */}
                        <Typography style={classes.subTitle}>
                            Materiais / Insumos
                        </Typography>

                        <div style={classes.sectionBox}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={12}>
                                <Typography style={classes.labelInput}>
                                    Selecione um ou mais materiais / insumos cadastrados:
                                </Typography>

                                <MultiSelect isMulti
                                    options={materialOptions}
                                    styles={styleMultiSelect}
                                    value={selectedMaterialOptions}
                                    onChange={handleSelectMaterials}
                                    placeholder=""
                                    noOptionsMessage={() => 'Nenhum material cadastrado'}
                                />
                            </Grid>
                        </Grid>

                        {materials.map((material, index) => (
                            <Grid container spacing={2} alignItems="center" key={material.materialId ?? index} style={{ marginTop: 4 }}>
                                <Grid item xs={12} md={6}>
                                    <Typography style={classes.labelInput}>
                                        Material:
                                    </Typography>

                                    <TextField
                                        style={classes.inputText}
                                        fullWidth
                                        value={`${material.name}${material.unit ? ` (${material.unit})` : ''}`}
                                        disabled
                                    />
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <Typography style={classes.labelInput}>
                                        Qtd. Prevista:
                                    </Typography>

                                    <TextField
                                        style={classes.inputText}
                                        fullWidth
                                        type='number'
                                        value={material.plannedQuantity}
                                        onChange={e => handleChangeMaterial(index, 'plannedQuantity', e.target.value)}
                                    />
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <Typography style={classes.labelInput}>
                                        Qtd. Utilizada:
                                    </Typography>

                                    <TextField
                                        style={classes.inputText}
                                        fullWidth
                                        type='number'
                                        value={material.usedQuantity}
                                        onChange={e => handleChangeMaterial(index, 'usedQuantity', e.target.value)}
                                    />
                                </Grid>
                            </Grid>
                        ))}
                        </div>

                        {/* 6. Equipamentos e veículos */}
                        <Typography style={classes.subTitle}>
                            Equipamentos / Veículos Alocados
                        </Typography>

                        <div style={classes.sectionBox}>
                        {allocatedAssets.map((asset, index) => (
                            <Grid container spacing={2} alignItems="center" key={index} style={{ marginBottom: 10 }}>
                                <Grid item xs={12} md={3}>
                                    <Typography style={classes.labelInput}>
                                        Equipamento / Veículo:
                                    </Typography>

                                    <TextField
                                        style={classes.inputText}
                                        fullWidth
                                        value={asset.description}
                                        onChange={e => handleChangeAsset(index, 'description', e.target.value)}
                                    />
                                </Grid>

                                <Grid item xs={12} md={2}>
                                    <Typography style={classes.labelInput}>
                                        Placa / Prefixo:
                                    </Typography>

                                    <TextField
                                        style={classes.inputText}
                                        fullWidth
                                        value={asset.plateOrPrefix}
                                        onChange={e => handleChangeAsset(index, 'plateOrPrefix', e.target.value)}
                                    />
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <Typography style={classes.labelInput}>
                                        Operador Responsável:
                                    </Typography>

                                    <TextField
                                        style={classes.inputText}
                                        fullWidth
                                        value={asset.operatorName}
                                        onChange={e => handleChangeAsset(index, 'operatorName', e.target.value)}
                                    />
                                </Grid>

                                <Grid item xs={12} md={2}>
                                    <Typography style={classes.labelInput}>
                                        Horas / Km Estimados:
                                    </Typography>

                                    <TextField
                                        style={classes.inputText}
                                        fullWidth
                                        type='number'
                                        value={asset.estimatedUsage}
                                        onChange={e => handleChangeAsset(index, 'estimatedUsage', e.target.value)}
                                    />
                                </Grid>

                                <Grid item xs={12} md={2}>
                                    <Typography style={classes.labelInput}>
                                        Horas / Km Utilizados:
                                    </Typography>

                                    <TextField
                                        style={classes.inputText}
                                        fullWidth
                                        type='number'
                                        value={asset.actualUsage}
                                        onChange={e => handleChangeAsset(index, 'actualUsage', e.target.value)}
                                    />
                                </Grid>
                            </Grid>
                        ))}

                        <Button
                            variant="outlined"
                            style={classes.buttonAdd}
                            onClick={() => setAllocatedAssets([...allocatedAssets, { ...emptyAsset }])}
                        >
                            Adicionar Equipamento
                        </Button>
                        </div>

                        {/* 7. Execução */}
                        <Typography style={classes.subTitle}>
                            Execução
                        </Typography>

                        <div style={classes.sectionBox}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={4}>
                                <SelectDate
                                    title="Data Início Efetivo: *"
                                    date={startedAt}
                                    setDate={setStartedAt}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <SelectDate
                                    title="Data Conclusão Efetiva: *"
                                    date={completedAt}
                                    setDate={setCompletedAt}
                                />
                            </Grid>


                            <Grid item xs={12} md={12}>
                                <Typography style={classes.labelInput}>
                                    Observações / Justificativas Técnicas do Encarregado de Campo: *
                                </Typography>

                                <TextField
                                    style={classes.inputText}
                                    fullWidth
                                    multiline
                                    rows={4}
                                    required
                                    value={technicalNotes}
                                    onChange={e => setTechnicalNotes(e.target.value)}
                                />
                            </Grid>
                        </Grid>
                        </div>
                    </CardContent>
                </Card>

                <Grid container spacing={2} alignItems="center" justifyContent="center">
                    <Grid item>
                        <Button
                            id='register-button'
                            variant="outlined"
                            type='submit'
                            startIcon={<FaEdit style={{ 'color': '#fff' }} />}
                            style={classes.buttonConfirm}
                        >
                            {isEdit ? 'Atualizar' : 'Cadastrar'}
                        </Button>
                    </Grid>
                </Grid>

                <div style={{ height: 40 }} />
            </form>

            <Notification
                notify={notify}
                setNotify={setNotify}
            />
        </div>
    );
}
